import React, { useState } from 'react';
import patientDocumentService from '../../../services/patientDocumentService';
import { FILE_BASE_URL } from '../../../services/api';
import outsourceService from '../../../services/outsourceService';

export const RecordOutsourceModal = ({
  isOpen,
  onClose,
  testItem,
  patient,
  onSuccess,
  showToast,
}) => {
  const [externalReferenceNo, setExternalReferenceNo] = useState(
    testItem?.externalReferenceNo || ''
  );
  const [externalResultSummary, setExternalResultSummary] = useState(
    testItem?.externalResultSummary || ''
  );
  const [attachmentUrl, setAttachmentUrl] = useState(
    testItem?.externalAttachmentUrl || testItem?.externalReportAttachmentUrl || ''
  );
  const [remarks, setRemarks] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!isOpen || !testItem) return null;

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('patientId', patient?.patientId || testItem.patientId || '');
      if (patient?.ledgerTransactionNo || testItem.ledgerTransactionNo) {
        formData.append(
          'ledgerTransactionNo',
          patient?.ledgerTransactionNo || testItem.ledgerTransactionNo
        );
      }
      formData.append('documentType', 'OUTSOURCE_REPORT');
      formData.append(
        'documentName',
        `${testItem.investigationName || 'Test'}_${testItem.referenceLabName || 'PartnerLab'}_Report`
      );

      const res = await patientDocumentService.uploadDocument(formData);
      if (res && res.data) {
        const fileUrl = res.data.viewUrl?.startsWith('http')
          ? res.data.viewUrl
          : `${FILE_BASE_URL}${res.data.viewUrl}`;
        setAttachmentUrl(fileUrl);
        showToast?.('Reference lab report uploaded successfully!', 'success');
      }
    } catch (err) {
      console.error('File upload error:', err);
      showToast?.(
        err.response?.data?.message || 'File upload failed. You may also paste a URL link.',
        'error'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();

    if (!externalResultSummary.trim() && !attachmentUrl.trim()) {
      showToast?.(
        'Please enter a clinical result summary or upload the partner report attachment.',
        'error'
      );
      return;
    }

    setSaving(true);
    try {
      await outsourceService.recordResult({
        testId: testItem.testId,
        externalReferenceNo: externalReferenceNo.trim() || null,
        externalResultSummary: externalResultSummary.trim() || 'Report attached. See partner document.',
        attachmentUrl: attachmentUrl.trim() || null,
        remarks: remarks.trim() || null,
      });

      showToast?.('Partner lab result and attachment saved successfully!', 'success');
      if (onSuccess) {
        onSuccess({
          testId: testItem.testId,
          externalReferenceNo: externalReferenceNo.trim(),
          externalResultSummary: externalResultSummary.trim() || 'Report attached. See partner document.',
          externalAttachmentUrl: attachmentUrl.trim(),
          outsourceStatus: 'RESULT_RECEIVED',
          resultFlag: 1,
        });
      }
      onClose();
    } catch (err) {
      console.error('Failed to save outsource result:', err);
      showToast?.(
        err.response?.data?.message || 'Failed to save external partner result.',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="path-modal-overlay">
      <div className="path-modal" style={{ maxWidth: '640px' }}>
        <div className="path-modal-header" style={{ background: '#f8fafc' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>
              📤 Record External Reference Lab Report
            </h3>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              Attach Softcopy (PDF) or Scanned Hardcopy (Letterhead) from Reference Lab
            </span>
          </div>
          <button type="button" className="path-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSave}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Context Card */}
            <div
              style={{
                background: '#f1f5f9',
                borderLeft: '4px solid #6366f1',
                padding: '12px 16px',
                borderRadius: '6px',
                fontSize: '0.85rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '8px',
              }}
            >
              <div>
                <strong>Patient:</strong> {patient?.patientName || testItem.patientName}
              </div>
              <div>
                <strong>UHID:</strong> {patient?.patientId || testItem.patientId}
              </div>
              <div>
                <strong>Test:</strong> {testItem.investigationName}
              </div>
              <div>
                <strong>Partner Lab:</strong> {testItem.referenceLabName || 'Reference Lab'}
              </div>
            </div>

            {/* External Reference No */}
            <div className="path-form-group">
              <label className="path-label">
                Reference Lab Report / Accession # <span style={{ color: '#94a3b8' }}>(Optional)</span>
              </label>
              <input
                type="text"
                className="path-input"
                placeholder="e.g. LAL-90218 or QDL-2026-99120"
                value={externalReferenceNo}
                onChange={(e) => setExternalReferenceNo(e.target.value)}
              />
              <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px' }}>
                The unique report number printed on the external partner's letterhead.
              </span>
            </div>

            {/* File Upload for Scanned Hardcopy or Softcopy PDF */}
            <div
              style={{
                border: '1px dashed #93c5fd',
                background: '#eff6ff',
                padding: '16px',
                borderRadius: '8px',
              }}
            >
              <label className="path-label" style={{ color: '#1e40af', fontWeight: '700' }}>
                📁 Upload External Report (PDF / Scanned Letterhead / JPG / PNG)
              </label>
              <input
                type="file"
                className="path-input"
                accept=".pdf,image/jpeg,image/png,image/jpg"
                onChange={handleFileUpload}
                disabled={uploading || saving}
                style={{ background: 'white' }}
              />
              <div style={{ fontSize: '0.78rem', color: '#3b82f6', marginTop: '6px' }}>
                💡 <strong>Hard Copy received?</strong> Scan the paper with your lab scanner to PDF/JPG and choose it here.
                <br />
                💡 <strong>Soft Copy received?</strong> Directly select the PDF downloaded from partner portal/email.
              </div>

              {uploading && (
                <div style={{ marginTop: '8px', color: '#2563eb', fontSize: '0.85rem' }}>
                  ⏳ Uploading report to patient repository...
                </div>
              )}

              {attachmentUrl && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    background: '#ffffff',
                    border: '1px solid #86efac',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ color: '#15803d', fontWeight: '600', fontSize: '0.82rem' }}>
                    ✅ Report Attached Successfully
                  </span>
                  <a
                    href={attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="path-attachment-pill"
                    style={{ fontSize: '0.78rem' }}
                  >
                    👁️ Preview Document ↗
                  </a>
                </div>
              )}
            </div>

            {/* Clinical Summary */}
            <div className="path-form-group">
              <label className="path-label">
                External Clinical Result / Findings Summary
              </label>
              <textarea
                className="path-input"
                rows={3}
                placeholder="Enter key diagnosis, numeric value, or impression (e.g. Hepatitis B Core IgM: Negative, HCV RNA: Not Detected)..."
                value={externalResultSummary}
                onChange={(e) => setExternalResultSummary(e.target.value)}
                style={{ resize: 'vertical' }}
              />
              <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px' }}>
                This summary will be visible in the patient's workbench and final diagnostic summary.
              </span>
            </div>

            {/* Optional Manual URL */}
            <div className="path-form-group">
              <label className="path-label" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Or Direct Report URL / Web Link <span style={{ color: '#94a3b8' }}>(Optional)</span>
              </label>
              <input
                type="text"
                className="path-input"
                placeholder="e.g. https://portal.questreference.com/reports/99120.pdf"
                value={attachmentUrl}
                onChange={(e) => setAttachmentUrl(e.target.value)}
                style={{ fontSize: '0.85rem' }}
              />
            </div>

            {/* Remarks */}
            <div className="path-form-group">
              <label className="path-label">
                Technician / Verification Remarks <span style={{ color: '#94a3b8' }}>(Optional)</span>
              </label>
              <input
                type="text"
                className="path-input"
                placeholder="e.g. Received via courier, verified against partner manifest"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          </div>

          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              background: '#f8fafc',
            }}
          >
            <button
              type="button"
              className="path-btn path-btn-secondary"
              onClick={onClose}
              disabled={saving || uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="path-btn path-btn-primary"
              disabled={saving || uploading}
            >
              {saving ? 'Saving...' : '💾 Save External Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordOutsourceModal;
